import { MessageAppend } from '@bitte-ai/types';
import { generateId } from 'ai';
import { Check, Copy } from 'lucide-react';
import React, { FC, memo, useState } from 'react';
import ReactMarkdown, { Options } from 'react-markdown';
import rehypeHighlight from 'rehype-highlight';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { useSmoothStreaming } from '../../hooks/useSmoothStreaming';
import { isMarkdownTableString } from '../../lib/regex';
import { cn } from '../../lib/utils';
import { MarkdownTable } from './MarkdownTable';

// Enhanced memoized markdown component that only re-renders when content actually changes
const MemoizedReactMarkdown: FC<Options> = memo(
  ReactMarkdown,
  (prevProps, nextProps) => {
    // Only re-render if content changed - this is the most expensive operation
    if (prevProps.children !== nextProps.children) return false;

    // Other props are less likely to change and less expensive to compare
    if (prevProps.className !== nextProps.className) return false;
    if (prevProps.remarkPlugins !== nextProps.remarkPlugins) return false;
    if (prevProps.rehypePlugins !== nextProps.rehypePlugins) return false;

    return true;
  },
);

const LinkRenderer = ({
  href,
  children,
  ...props
}: React.AnchorHTMLAttributes<HTMLAnchorElement>) => {
  return (
    <a
      href={href as string}
      target="_blank"
      rel="noopener noreferrer"
      className="bitte-text-blue-300 hover:bitte-text-white bitte-underline bitte-transition-colors bitte-duration-200"
      {...props}
    >
      {children}
    </a>
  );
};

const HeadingRenderer = ({ level, children, ...props }: any) => {
  const headingClasses = {
    1: 'bitte-text-xl bitte-font-bold bitte-text-blue-50 bitte-mb-4 bitte-pb-2',
    2: 'bitte-text-[19px] bitte-font-semibold bitte-text-blue-50 bitte-mb-3 bitte-mt-6',
    3: 'bitte-text-base bitte-font-medium bitte-text-white bitte-mb-2 bitte-mt-4',
    4: 'bitte-text-sm bitte-font-medium bitte-text-blue-200 bitte-mb-2',
    5: 'bitte-text-[12px] bitte-font-medium bitte-text-blue-300 bitte-mb-1',
    6: 'bitte-text-[12px] bitte-font-normal bitte-text-blue-400 bitte-mb-1',
  };

  const Tag = `h${level}` as keyof JSX.IntrinsicElements;

  return (
    <Tag
      className={headingClasses[level as keyof typeof headingClasses]}
      {...props}
    >
      {children}
    </Tag>
  );
};

const ListRenderer = ({ ordered, children, ...props }: any) => {
  const Tag = ordered ? 'ol' : 'ul';
  return (
    <Tag
      className={cn(
        'bitte-space-y-1 bitte-mb-4 bitte-mt-2',
        ordered
          ? 'bitte-list-decimal bitte-list-inside '
          : 'bitte-list-disc bitte-list-inside ',
      )}
      {...props}
    >
      {children}
    </Tag>
  );
};

const ListItemRenderer = ({ children, ...props }: any) => {
  // Check if children contain paragraph elements and flatten them
  const flattenedChildren = React.Children.map(children, (child) => {
    // If child is a paragraph element, return its children instead
    if (React.isValidElement(child) && child.type === 'p') {
      const paragraphElement = child as React.ReactElement<{
        children: React.ReactNode;
      }>;
      return paragraphElement.props.children;
    }
    return child;
  });

  return (
    <li
      className="bitte-text-white bitte-text-[14px] bitte-leading-relaxed bitte-block bitte-break-words"
      style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}
      {...props}
    >
      {flattenedChildren}
    </li>
  );
};

// Enhanced component to handle clickable example text
const ExampleTextRenderer = ({
  content,
  onAppend,
}: {
  content: string;
  onAppend?: MessageAppend;
}) => {
  // Regex to match example patterns like:
  // (For example: "Swap 0.1 ETH to USDC on Arbitrum.")
  // (for example: "Supply USDT," "Borrow NEAR," "Swap tokens," or "Show my portfolio")
  const examplePattern = /\((For example|for example):\s*([^)]+)\)/gi;

  type ProcessedPart =
    | string
    | {
        type: 'clickable';
        text: string;
        content: string;
        key: string;
      };

  const processExampleText = (text: string): ProcessedPart[] => {
    const parts: ProcessedPart[] = [];
    let lastIndex = 0;
    let match;

    match = examplePattern.exec(text);
    while (match !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }

      // Process the example content
      const exampleContent = match[2];
      const quotedTexts = exampleContent.match(/"([^"]+)"/g) || [];

      // Build the example section
      const exampleStart = `(${match[1]}: `;
      const exampleEnd = ')';

      parts.push(exampleStart);

      // Process quoted texts and make them clickable
      let exampleLastIndex = 0;
      const tempContent = exampleContent;

      quotedTexts.forEach((quotedText, index) => {
        const unquotedText = quotedText.replace(/"/g, '');
        const quotedIndex = tempContent.indexOf(quotedText, exampleLastIndex);

        // Add text before the quoted text
        if (quotedIndex > exampleLastIndex) {
          parts.push(tempContent.substring(exampleLastIndex, quotedIndex));
        }

        // Add clickable quoted text
        parts.push({
          type: 'clickable',
          text: quotedText,
          content: unquotedText,
          key: `example-${index}-${Math.random()}`,
        });

        exampleLastIndex = quotedIndex + quotedText.length;
      });

      // Add remaining text after the last quoted text
      if (exampleLastIndex < exampleContent.length) {
        parts.push(exampleContent.substring(exampleLastIndex));
      }

      parts.push(exampleEnd);
      lastIndex = match.index + match[0].length;
      match = examplePattern.exec(text);
    }

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }

    return parts;
  };

  const handleExampleClick = (exampleText: string) => {
    if (onAppend) {
      onAppend({
        id: generateId(),
        role: 'user',
        content: exampleText,
      });
    }
  };

  const processedParts = processExampleText(content);

  return (
    <span>
      {processedParts.map((part, index) => {
        if (typeof part === 'string') {
          return <span key={index}>{part}</span>;
        } else if (part.type === 'clickable') {
          return (
            <button
              key={part.key}
              type="button"
              onClick={() => handleExampleClick(part.content)}
              className="bitte-text-blue-400 hover:bitte-text-blue-300 bitte-underline bitte-cursor-pointer bitte-transition-colors bitte-duration-200 bitte-bg-transparent bitte-border-none bitte-p-0 bitte-font-inherit"
              title={`Click to try: ${part.content}`}
            >
              {part.text}
            </button>
          );
        }
        return null;
      })}
    </span>
  );
};

// Updated ParagraphRenderer to handle example text
const ParagraphRenderer = ({ children, onAppend, ...props }: any) => {
  const content = React.Children.toArray(children).join('');
  const hasExamplePattern = /\((For example|for example):/i.test(content);

  if (hasExamplePattern && onAppend) {
    return (
      <p
        className="bitte-text-white bitte-text-[14px] bitte-block bitte-leading-relaxed bitte-break-words"
        style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}
        {...props}
      >
        <ExampleTextRenderer content={content} onAppend={onAppend} />
      </p>
    );
  }

  return (
    <p
      className="bitte-text-white bitte-text-[14px]  bitte-block bitte-leading-relaxed bitte-break-words"
      style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}
      {...props}
    >
      {children}
    </p>
  );
};

const CodeRenderer = ({ inline, children, className }: any) => {
  const [isCopied, setIsCopied] = useState(false);

  // Helper function to recursively extract text content from React elements
  const extractTextContent = (node: any): string => {
    if (typeof node === 'string') {
      return node;
    }
    if (typeof node === 'number') {
      return String(node);
    }
    if (Array.isArray(node)) {
      return node.map(extractTextContent).join('');
    }
    if (React.isValidElement(node)) {
      const element = node as React.ReactElement<{
        children?: React.ReactNode;
      }>;
      if (typeof element.props.children !== 'undefined') {
        return extractTextContent(element.props.children);
      }
    }
    return '';
  };

  const handleCopy = async () => {
    try {
      const textContent = extractTextContent(children);
      await navigator.clipboard.writeText(textContent.trim());
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000); // Hide after 2 seconds
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  // Extract language from className (e.g., "language-javascript" -> "javascript")
  const language = (className?.replace('language-', '') || 'text')
    .replace(/^hljs[\s-]*/i, '') // Remove hljs prefix
    .replace(/^language[\s-]*/i, ''); // Remove language prefix

  if (inline || language === 'text' || language === '') {
    return (
      <code className="bitte-text-blue-200 bitte-px-2 bitte-py-1 bitte-rounded bitte-text-[12px] bitte-font-mono">
        {children}
      </code>
    );
  }

  return (
    <div className="bitte-relative bitte-my-6 bitte-group">
      <div className="bitte-bg-blue-950/80 bitte-border bitte-border-blue-400/30 bitte-rounded-lg bitte-overflow-hidden">
        {/* Language label and copy button header */}
        <div className="bitte-flex bitte-items-center bitte-justify-between bitte-px-4 bitte-py-2 bitte-bg-blue-900/50 bitte-border-b bitte-border-blue-400/20">
          <span className="bitte-text-blue-300 bitte-text-[12px] bitte-font-medium bitte-uppercase bitte-tracking-wide">
            {language}
          </span>
          <button
            type="button"
            onClick={handleCopy}
            className="bitte-flex bitte-items-center bitte-gap-1 bitte-px-2 bitte-py-1 bitte-text-[12px] bitte-text-blue-300 hover:bitte-text-blue-200 bitte-bg-blue-800/50 hover:bitte-bg-blue-700/50 bitte-border bitte-border-blue-400/30 bitte-rounded bitte-transition-all bitte-duration-200"
            title="Copy code"
          >
            {isCopied ? (
              <>
                <Check className="bitte-w-3 bitte-h-3" />
                <span>Copied!</span>
              </>
            ) : (
              <>
                <Copy className="bitte-w-3 bitte-h-3" />
                <span>Copy</span>
              </>
            )}
          </button>
        </div>

        {/* Code content */}
        <div
          className="bitte-p-4 bitte-overflow-x-auto bitte-m-0"
          style={{
            background: 'transparent !important',
            backgroundColor: 'transparent !important',
            backgroundImage: 'none !important',
          }}
        >
          <code
            className={`bitte-bg-blue-950/80 bitte-text-white bitte-text-[13px] bitte-font-mono bitte-leading-relaxed bitte-whitespace-pre bitte-block ${language ? `language-${language}` : ''}`}
            style={{
              background: 'rgb(30 41 59 / 0.8) !important',
              backgroundColor: 'rgb(30 41 59 / 0.8) !important',
              whiteSpace: 'pre',
              display: 'block',
              border: 'none !important',
              borderRadius: '0 !important',
              padding: '0 !important',
            }}
          >
            {children}
          </code>
        </div>
      </div>
    </div>
  );
};

const BlockquoteRenderer = ({ children, ...props }: any) => {
  return (
    <blockquote
      className="bitte-border-l-4 bitte-border-blue-500 bitte-pl-4 bitte-my-4 bitte-italic bitte-text-blue-200 bitte-bg-blue-950/30 bitte-py-2"
      {...props}
    >
      {children}
    </blockquote>
  );
};

// Table components for ReactMarkdown
const TableRenderer = ({ children, ...props }: any) => {
  return (
    <div className="bitte-relative bitte-w-full bitte-overflow-auto">
      <table
        className="bitte-mt-4 bitte-w-full bitte-caption-bottom bitte-text-sm"
        {...props}
      >
        {children}
      </table>
    </div>
  );
};

const TableHeaderRenderer = ({ children, ...props }: any) => {
  return (
    <thead className="[&_tr]:bitte-border-b" {...props}>
      {children}
    </thead>
  );
};

const TableBodyRenderer = ({ children, ...props }: any) => {
  return (
    <tbody className="[&_tr:last-child]:bitte-border-0" {...props}>
      {children}
    </tbody>
  );
};

const TableRowRenderer = ({ children, ...props }: any) => {
  return (
    <tr className="bitte-border-none hover:bitte-bg-transparent" {...props}>
      {children}
    </tr>
  );
};

const TableCellRenderer = ({ children, ...props }: any) => {
  return (
    <td
      className="bitte-whitespace-nowrap bitte-px-4 bitte-text-left bitte-p-2 bitte-align-middle"
      {...props}
    >
      <div className="bitte-max-w-[250px] bitte-break-words">
        <div className="bitte-truncate">{children}</div>
      </div>
    </td>
  );
};

const TableHeadCellRenderer = ({ children, ...props }: any) => {
  return (
    <th
      className="bitte-whitespace-nowrap bitte-px-4 bitte-text-left text-[14px] bitte-font-medium bitte-h-10 bitte-align-middle bitte-text-muted-foreground"
      {...props}
    >
      {children}
    </th>
  );
};

// HR renderer that doesn't render anything
const HRRenderer = () => {
  return null;
};

// Pre renderer to handle the outer pre element created by rehype-highlight
const PreRenderer = ({ children, ...props }: any) => {
  return (
    <pre
      className="bitte-bg-transparent"
      style={{
        background: 'transparent !important',
        backgroundColor: 'transparent !important',
      }}
      {...props}
    >
      {children}
    </pre>
  );
};

const renderTableWithExtraContent = (content: string) => {
  const lines = content.split('\n');
  const tableLines: string[] = [];
  const extraLines: string[] = [];

  for (const line of lines) {
    const isTableLine =
      line.trim().startsWith('|') || line.trim().match(/^\|?[\s\-\|]+\|?$/);
    if (isTableLine) {
      tableLines.push(line);
    } else if (line.trim() !== '') {
      extraLines.push(line);
    }
  }

  const tableContent = tableLines.join('\n');
  const extraContent = extraLines.join('\n').trim();

  return (
    <div>
      {tableContent && <MarkdownTable content={tableContent} />}

      {extraContent && (
        <div className="bitte-mt-4">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeHighlight, rehypeRaw]}
            components={{
              a: ({ href, children, ...props }: any) => (
                <a
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  className="bitte-text-blue-300 hover:bitte-text-blue-200 bitte-underline"
                  {...props}
                >
                  {children}
                </a>
              ),
            }}
          >
            {extraContent}
          </ReactMarkdown>
        </div>
      )}
    </div>
  );
};

export const ImprovedMessage = memo(
  ({
    content,
    onAppend,
    isStreaming = false,
  }: {
    content: string;
    onAppend?: MessageAppend;
    isComplete?: boolean;
    isStreaming?: boolean;
  }) => {
    // Create ultra-smooth character-by-character streaming animation
    const smoothContent = useSmoothStreaming(content, isStreaming, 3);

    // Optimize rendering based on streaming state: simple text during streaming, full markdown when done
    const shouldUseSimpleRendering = isStreaming;

    const cleanContent = smoothContent
      .replace(/^"/, '') // remove first quote
      .replace(/"$/, '') // remove last quote
      .replace(/^`([\s\S]*)`$/g, '$1') // remove backticks that wrap entire content
      .replace(/\\n/g, '\n')
      .replace(/`([A-Za-z][A-Za-z0-9]*)`/g, '$1') // remove backticks around single words that look like tokens
      .replace(/`(st[A-Z][A-Za-z]*|li[A-Z][A-Za-z]*)`/g, '$1') // specifically handle stNEAR, liNEAR type tokens
      .replace(/\\`/g, '`') // unescape escaped backticks
      .replace(/\n{3,}/g, '\n\n') // replace multiple newlines with double newlines
      .replace(/<img\s+src="([^"]+)"[^>]*>/gi, '![]($1)') // convert HTML img tags to markdown (case insensitive)
      .replace(/<img\s+src='([^']+)'[^>]*>/gi, '![]($1)') // handle single quotes too
      .trim(); // remove leading/trailing whitespace

    // Check if this is table content - but skip expensive processing during streaming
    if (!isStreaming && isMarkdownTableString(cleanContent)) {
      // Only render tables when streaming is complete
      return renderTableWithExtraContent(cleanContent);
    }

    // During streaming: show simple formatted text for smooth performance
    if (shouldUseSimpleRendering) {
      return (
        <div
          className="bitte-text-white bitte-text-[14px] bitte-leading-relaxed bitte-whitespace-pre-wrap"
          style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}
        >
          {cleanContent}
        </div>
      );
    }

    // After streaming: full markdown rendering
    return (
      <div
        className="[&_code]:bitte-text-blue-200 [&_code]:bitte-px-2 [&_code]:bitte-py-1 [&_code]:bitte-rounded [&_code]:bitte-text-[12px] [&_code]:bitte-font-mono [&_.hljs-keyword]:bitte-text-purple-400 [&_.hljs-string]:bitte-text-emerald-400 [&_.hljs-comment]:bitte-text-gray-500 [&_.hljs-number]:bitte-text-orange-400 [&_.hljs-attr]:bitte-text-cyan-400 [&_.hljs-function]:bitte-text-yellow-400 [&_.hljs-variable]:bitte-text-blue-300 [&_.hljs-title]:bitte-text-pink-400"
        style={{
          ['--code-background' as any]: 'rgb(30 41 59 / 0.8)',
        }}
      >
        <style>
          {`

            .bitte-max-w-full pre , .bitte-max-w-full code{
              background: transparent !important;
              background-color: transparent  !important;
              border: none !important;
            }
          `}
        </style>
        <MemoizedReactMarkdown
          className="bitte-max-w-full"
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[
            rehypeRaw,
            [
              rehypeHighlight,
              {
                detect: true,
                ignoreMissing: true,
                subset: false,
              },
            ],
          ]}
          components={{
            a: LinkRenderer,
            h1: (props) => <HeadingRenderer level={1} {...props} />,
            h2: (props) => <HeadingRenderer level={2} {...props} />,
            h3: (props) => <HeadingRenderer level={3} {...props} />,
            h4: (props) => <HeadingRenderer level={4} {...props} />,
            h5: (props) => <HeadingRenderer level={5} {...props} />,
            h6: (props) => <HeadingRenderer level={6} {...props} />,
            p: (props) => <ParagraphRenderer {...props} onAppend={onAppend} />,
            ul: (props) => <ListRenderer ordered={false} {...props} />,
            ol: (props) => <ListRenderer ordered={true} {...props} />,
            li: ListItemRenderer,
            code: (props: any) => <CodeRenderer {...props} />,
            pre: PreRenderer,
            blockquote: BlockquoteRenderer,
            img: ({ src, alt }) =>
              src ? (
                <img
                  src={src}
                  alt={alt || `${src} image`}
                  className="bitte-max-w-[70px] bitte-mr-3 bitte-rounded-[5px] bitte-inline-block bitte-align-top"
                />
              ) : (
                <></>
              ),
            table: TableRenderer,
            thead: TableHeaderRenderer,
            tbody: TableBodyRenderer,
            tr: TableRowRenderer,
            td: TableCellRenderer,
            th: TableHeadCellRenderer,
            hr: HRRenderer,
          }}
        >
          {cleanContent}
        </MemoizedReactMarkdown>
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Fast content comparison - most important for streaming performance
    if (prevProps.content !== nextProps.content) return false;

    // Check streaming status - this affects rendering mode
    if (prevProps.isStreaming !== nextProps.isStreaming) return false;

    // Check completion status if provided
    if (prevProps.isComplete !== nextProps.isComplete) return false;

    // Function reference comparison for onAppend
    if (prevProps.onAppend !== nextProps.onAppend) return false;

    return true;
  },
);

ImprovedMessage.displayName = 'ImprovedMessage';
